import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import axios from "axios";
import { formatDateDDMMYYYY } from "../utils/dateUtils";
import { convertTo12HourFormat } from "../utils/timeUtils";
import { FaUser, FaPhone, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
import {
  calculateCabPrice,
  calculateTaxesAndFees,
  calculateTotalAmount,
} from "../utils/priceCalculator";

const CustomerDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [cabDetails, setCabDetails] = useState(null);
  const [distance, setDistance] = useState(null);
  const [rentalPackages, setRentalPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Form state
  const [fullName, setFullName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [pickupAddress, setPickupAddress] = useState("");
  const [dropAddress, setDropAddress] = useState("");
  const [pickupAddressCity, setPickupAddressCity] = useState("");
  const [dropAddressCity, setDropAddressCity] = useState("");
  const [errors, setErrors] = useState({});

  // Extract query parameters
  const queryParams = new URLSearchParams(location.search);
  const fromCity = queryParams.get("from");
  const toCity = queryParams.get("to");
  const urlPickup = queryParams.get("pickupAddress") || "";
  const urlDrop = queryParams.get("dropAddress") || "";
  const dbPickup = queryParams.get("dbPickup") || "";
  const dbDrop = queryParams.get("dbDrop") || "";
  const fromParam = queryParams.get("fromParam") || "";
  const toParam = queryParams.get("toParam") || "";
  const pickupAddressCityParam = queryParams.get("pickupAddressCity") || "";
  const dropAddressCityParam = queryParams.get("dropAddressCity") || "";

  // Set initial pickup and drop addresses based on available data
  useEffect(() => {
    // Priority order for pickup address:
    // 1. URL pickup address
    // 2. Database pickup address
    // 3. From parameter
    if (urlPickup) {
      setPickupAddress(urlPickup);
    } else if (dbPickup) {
      setPickupAddress(dbPickup);
    } else if (fromParam) {
      setPickupAddress(fromParam);
    }

    // Priority order for drop address:
    // 1. URL drop address
    // 2. Database drop address
    // 3. To parameter
    if (urlDrop) {
      setDropAddress(urlDrop);
    } else if (dbDrop) {
      setDropAddress(dbDrop);
    } else if (toParam) {
      setDropAddress(toParam);
    }

    // Set initial values for city-specific addresses with proper fallbacks
    setPickupAddressCity(pickupAddressCityParam || fromCity || "");
    setDropAddressCity(dropAddressCityParam || toCity || "");
  }, [
    urlPickup,
    urlDrop,
    dbPickup,
    dbDrop,
    fromParam,
    toParam,
    pickupAddressCityParam,
    dropAddressCityParam,
  ]);

  let distanceParam = 0;

  // Special handling for known routes with fixed distances
  const fromLower = fromCity?.toLowerCase();
  const toLower = toCity?.toLowerCase();

  // Check for Rajkot-Delhi route specifically
  if (
    (fromLower === "rajkot" && toLower === "delhi") ||
    (fromLower === "delhi" && toLower === "rajkot")
  ) {
    distanceParam = 1140; // Known distance between Rajkot and Delhi
    console.log(
      `Using known distance for ${fromLower}-${toLower} route: ${distanceParam}km`,
    );
  } else {
    // For other routes, get from URL param and preserve decimal places
    const distanceStr = queryParams.get("distance");
    distanceParam = distanceStr ? parseFloat(distanceStr) : 0;
    console.log(`Using distance from URL: ${distanceParam}km`);
  }

  const cabId = queryParams.get("cabId");
  const bookingType = queryParams.get("tripType") || queryParams.get("type");
  const travelDate = queryParams.get("date");
  const travelTime = queryParams.get("time");
  const serviceType = queryParams.get("service"); // rental, airport, or null for outstation
  const packageName = decodeURIComponent(queryParams.get("package") || ""); // for rental services
  const cityName = decodeURIComponent(queryParams.get("city") || ""); // for rental services
  const airportDirection = queryParams.get("direction"); // "to" or "from" for airport transfers
  const airportCode = queryParams.get("airport"); // airport code for airport transfers

  // Debug logging
  console.log("🚀 CustomerDetails URL params:", {
    serviceType,
    packageName,
    cityName,
    fromCity,
    toCity,
    airportDirection,
    airportCode,
    fullURL: location.search,
  });

  // Import API URL from config for consistency
  const { API_URL } = require("../config/apiConfig");

  // Get user from Redux state
  const authState = useSelector((state) => state.auth);
  const user = authState.user?.user || authState.user; // Handle both nested and direct user object

  // Auto-fill user details from the logged-in user or from saved data
  useEffect(() => {
    console.log("=== Auto-fill Debug Start ===");
    console.log("Full auth state:", authState);
    console.log("Extracted user:", user);

    // First priority: Use authenticated user data from Redux
    if (user && (user.phone || user.mobile)) {
      const userPhone = user.phone || user.mobile;
      console.log("1. Using Redux user data - Phone:", userPhone);
      setMobile(userPhone);
      localStorage.setItem("userMobile", userPhone);

      // Set other user details if available
      if (user.name) setFullName(user.name);
      if (user.email) setEmail(user.email);
      return; // Exit early since we have the most reliable data
    }

    // Second priority: Check localStorage for saved mobile
    const savedMobile = localStorage.getItem("userMobile");
    if (savedMobile) {
      console.log("2. Using mobile from localStorage:", savedMobile);
      setMobile(savedMobile);

      // Set other saved user details
      const savedName = localStorage.getItem("userName");
      const savedEmail = localStorage.getItem("userEmail");
      if (savedName) setFullName(savedName);
      if (savedEmail) setEmail(savedEmail);
      return; // Exit if we found saved mobile
    }

    // Third priority: Check for user object in localStorage
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        console.log("3. Found user in localStorage:", parsedUser);

        // Check for phone in different possible locations
        const userPhone =
          parsedUser.phone || parsedUser.mobile || parsedUser.phoneNumber;
        if (userPhone) {
          console.log(
            "Setting mobile from localStorage user object:",
            userPhone,
          );
          setMobile(userPhone);
          localStorage.setItem("userMobile", userPhone);

          // Also set name and email if available
          if (parsedUser.name) setFullName(parsedUser.name);
          if (parsedUser.email) setEmail(parsedUser.email);
          return;
        }
      } catch (e) {
        console.error("Error parsing user data from localStorage:", e);
      }
    }

    // Fourth priority: Try to get mobile from query params
    const queryMobile = queryParams.get("mobile");
    if (queryMobile) {
      console.log("4. Setting mobile from query params:", queryMobile);
      setMobile(queryMobile);
      localStorage.setItem("userMobile", queryMobile);
    }

    console.log("=== Auto-fill Debug End ===");
  }, [user, authState]); // Re-run when user or authState changes

  // Separate useEffect for cab details to avoid dependency issues
  useEffect(() => {
    // Get cab details from the backend API
    const getCabDetails = async () => {
      if (!cabId) return;

      setIsLoading(true);

      try {
        // First, try to get data from localStorage (set in CarSelection page)
        const savedCabData = localStorage.getItem("selectedCabData");
        let parsedData = null;

        if (savedCabData) {
          try {
            parsedData = JSON.parse(savedCabData);
          } catch (e) {
            console.error("Error parsing saved cab data:", e);
          }
        }

        // Make API call to get cab details
        const response = await axios.get(`${API_URL}/cab/${cabId}`);

        if (response.data.success) {
          const cabData = response.data.data;

          // Validate and potentially adjust the basePrice for long-distance routes
          if (distanceParam > 100) {
            // Determine the appropriate per-km rate based on cab category
            let baseRatePerKm = 12; // Default standard rate

            // Look for category indicators in the cab data
            const cabCategory = (cabData.category || "").toLowerCase();
            const cabName = (cabData.name || "").toLowerCase();

            if (cabCategory.includes("luxury") || cabName.includes("luxury")) {
              baseRatePerKm = 15; // Luxury rate
            } else if (cabCategory.includes("suv") || cabName.includes("suv")) {
              baseRatePerKm = 14; // SUV rate
            }

            // Calculate a realistic minimum fare based on distance and rate
            const minRealisticPrice = Math.round(distanceParam * baseRatePerKm);

            // If the current basePrice is unrealistically low (less than 80% of calculated),
            // replace it with the realistic calculated price
            if (cabData.basePrice < minRealisticPrice * 0.8) {
              console.warn(
                `Cab price ${cabData.basePrice} is too low for ${distanceParam}km journey. ` +
                  `Adjusting to realistic price: ${minRealisticPrice}`,
              );

              // Update both basePrice and price for consistency
              cabData.basePrice = minRealisticPrice;
              cabData.price = minRealisticPrice;
            }
          }

          // Calculate price dynamically based on cab and distance
          const cabPriceDynamic = distance
            ? calculateCabPrice(cabData, distance)
            : cabData.baseKmPrice;

          // Extract and properly parse the price from URL params
          let amount = 0;
          try {
            // Handle amount parameter which might be numeric or a string
            const amountParam = queryParams.get("amount");
            if (amountParam) {
              // Ensure it's converted to a proper number
              amount = parseFloat(amountParam);

              // Validate the amount - apply a realistic minimum for long distance trips
              if (distance > 100 && amount < 1000) {
                console.warn(
                  `Amount from URL (${amount}) appears too low for a ${distance}km trip. Using calculated price.`,
                );
                amount = 0; // Force using the dynamic calculation below
              }
            }
          } catch (e) {
            console.error("Error parsing amount parameter:", e);
          }

          setCabDetails({
            _id: cabData._id,
            name: cabData.name,
            // Use price in order of preference: dynamic calculation, URL amount, base price
            basePrice:
              cabPriceDynamic || (amount > 0 ? amount : cabData.baseKmPrice),
            imageUrl:
              cabData.imageUrl || (parsedData ? parsedData.imageUrl : ""),
            description: cabData.description,
            seatingCapacity: cabData.seatingCapacity,
            acType: cabData.acType,
            luggageCapacity: cabData.luggageCapacity,
            includedKm: cabData.includedKm,
            extraFarePerKm: cabData.extraFarePerKm,
            fuelChargesIncluded: cabData.fuelChargesIncluded,
            driverChargesIncluded: cabData.driverChargesIncluded,
            nightChargesIncluded: cabData.nightChargesIncluded,
          });
        } else {
          toast.error("Could not retrieve cab details");
          // Fallback to URL parameters
          const cabName = queryParams.get("cabName");
          const amount = queryParams.get("amount");
          setCabDetails({
            _id: cabId,
            name: cabName || "Selected Cab",
            basePrice: amount ? parseInt(amount) : 3000,
            imageUrl: parsedData?.imageUrl || "",
          });
        }
      } catch (error) {
        console.error("Error fetching cab details:", error);
        toast.error("Error fetching cab details. Using cached data.");

        // Fallback to URL parameters
        const cabName = queryParams.get("cabName");

        // Parse amount properly with validation
        let amount = 0;
        try {
          const amountParam = queryParams.get("amount");
          if (amountParam) {
            amount = parseFloat(amountParam);

            // Apply realistic minimum for long distance trips
            if (distanceParam > 100 && amount < 1000) {
              const minPerKmRate = 12; // Standard rate per km
              amount = Math.round(distanceParam * minPerKmRate);
              console.warn(
                `Amount from URL (${amountParam}) appears too low for a ${distanceParam}km trip. Using calculated amount: ₹${amount}`,
              );
            }
          }
        } catch (e) {
          console.error("Error parsing amount in fallback:", e);
        }

        setCabDetails({
          _id: cabId,
          name: cabName || "Selected Cab",
          basePrice: amount > 0 ? amount : 3000, // Default to 3000 if no valid amount
          imageUrl: "",
        });
      } finally {
        setIsLoading(false);
      }
    };

    getCabDetails();
  }, [cabId]); // Only depend on cabId, not the entire queryParams object

  // Fetch rental packages for correct pricing (for rental services)
  useEffect(() => {
    const fetchRentalPackages = async () => {
      if (serviceType === "rental") {
        try {
          console.log("🎁 Fetching rental packages for pricing...");
          const response = await axios.get(`${API_URL}/rental-package`);
          if (response.data.success) {
            setRentalPackages(response.data.data);

            // Find the selected package by name
            const foundPackage = response.data.data.find(
              (pkg) => pkg.name === packageName,
            );
            if (foundPackage) {
              console.log("🎁 Found rental package:", foundPackage);
              setSelectedPackage(foundPackage);
            }
          }
        } catch (error) {
          console.error("Error fetching rental packages:", error);
        }
      }
    };

    fetchRentalPackages();
  }, [serviceType, packageName]);

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    // Clear previous errors
    setErrors({});

    // Validate full name
    if (!fullName.trim()) {
      newErrors.fullName = "Full name is required";
      isValid = false;
    }

    // Validate mobile number
    if (!mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
      isValid = false;
    } else if (!/^[0-9]{10}$/.test(mobile.trim())) {
      newErrors.mobile = "Please enter a valid 10-digit mobile number";
      isValid = false;
    }

    // Validate email if provided (optional)
    if (email && !/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // Validate pickup address (more lenient for rental and airport services)
    if (!pickupAddress.trim()) {
      if (serviceType === "airport") {
        // For airport services, just require some pickup location
        newErrors.pickupAddress =
          "Please provide pickup location for airport transfer";
      } else if (serviceType === "rental") {
        // For rental services, just require some pickup location
        newErrors.pickupAddress =
          "Please provide pickup location for rental service";
      } else {
        newErrors.pickupAddress = "Pickup address is required";
      }
      isValid = false;
    }

    // Only set errors if there are any
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      // Scroll to the first error
      const firstErrorField = Object.keys(newErrors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }

    return isValid;
  };

  // Function to calculate rental pricing from package data
  const calculateRentalPricing = () => {
    if (serviceType !== "rental" || !selectedPackage || !cabDetails) {
      return null;
    }

    let totalPriceFromPackage = cabDetails?.basePrice || 5;
    let foundPrice = false;

    // Check for NEW serviceCities structure first (from admin panel)
    if (
      selectedPackage.serviceCities &&
      Array.isArray(selectedPackage.serviceCities)
    ) {
      selectedPackage.serviceCities.forEach((serviceCity) => {
        if (serviceCity.cabPrices) {
          const currentCabName = cabDetails?.name || "";
          const cabPrices = serviceCity.cabPrices;
          const price =
            cabPrices[currentCabName] ||
            cabPrices[currentCabName.toUpperCase()] ||
            cabPrices[currentCabName.toLowerCase()] ||
            cabPrices[currentCabName.replace(/\s*\(AC\)/i, "")] ||
            cabPrices[currentCabName.replace(/\s*\(AC\)/i, "").toUpperCase()];

          if (price && price > 0) {
            totalPriceFromPackage = price;
            foundPrice = true;
            return;
          }
        }
      });
    }

    if (foundPrice) {
      // The price from package is the actual base price
      // Calculate tax as 18% GST of the base price
      const basePrice = totalPriceFromPackage;
      const taxRate = 0.18;
      let taxes = Math.round(basePrice * taxRate);

      // Ensure minimum tax of ₹1
      if (taxes === 0 && basePrice > 0) {
        taxes = 1;
      }

      const totalAmount = basePrice + taxes;

      console.log(
        `🎯 Calculated rental pricing: Base ₹${basePrice}, Tax ₹${taxes}, Total ₹${totalAmount}`,
      );

      return {
        basePrice,
        taxes,
        totalAmount,
        subtotal: basePrice,
      };
    }

    return null;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Reset any previous errors
    setErrors({});

    if (!validateForm()) {
      toast.error("Please check the form for errors");
      return;
    }

    // Save user details to localStorage for future bookings
    localStorage.setItem("userName", fullName);
    localStorage.setItem("userMobile", mobile);
    if (email) localStorage.setItem("userEmail", email);

    // Prepare all data needed for payment
    const params = new URLSearchParams();

    // Basic booking details
    params.append("from", fromCity);
    params.append("to", toCity);
    params.append("cabId", cabId);
    params.append("tripType", bookingType);
    params.append("name", fullName);
    params.append("mobile", mobile);

    // Service type specific parameters
    if (serviceType) params.append("service", serviceType);
    if (packageName) params.append("package", packageName);
    if (cityName) params.append("city", cityName);

    // Location data - preserve all sources
    params.append("pickupAddress", pickupAddress);
    params.append("dropAddress", dropAddress);
    params.append("fromParam", fromParam);
    params.append("toParam", toParam);
    params.append("dbPickup", dbPickup);
    params.append("dbDrop", dbDrop);
    params.append("dbFrom", queryParams.get("dbFrom") || "");
    params.append("dbTo", queryParams.get("dbTo") || "");

    // New city-specific addresses
    params.append("pickupAddressCity", pickupAddressCity);
    params.append("dropAddressCity", dropAddressCity);

    // Additional booking details
    if (email) params.append("email", email);
    if (travelDate) params.append("date", travelDate);
    if (travelTime) params.append("time", travelTime);
    params.append("distance", distanceParam.toString());

    // Add via cities if present
    const viaCities = queryParams.get("via");
    if (viaCities) params.append("via", viaCities);

    // Service-specific parameters
    if (serviceType === "airport") {
      if (airportDirection) params.append("direction", airportDirection);
      if (airportCode) params.append("airport", airportCode);
    } else if (serviceType === "rental") {
      // For rental services, ensure we have the package info
      if (packageName) {
        params.append("package", encodeURIComponent(packageName));
      }
      // For rental, 'to' is usually same as 'from' (local service)
      if (!toCity) {
        params.append("to", fromCity);
      }
    }

    // Calculate and validate amount based on distance and cab details
    if (cabDetails) {
      // For rental services, use calculated pricing from package data instead of URL amount
      const rentalPricing = calculateRentalPricing();

      let subtotal = 0;
      const urlAmount = queryParams.get("amount")
        ? parseFloat(queryParams.get("amount"))
        : 0;

      if (serviceType === "rental" && rentalPricing) {
        // Use calculated rental pricing instead of URL amount
        console.log(
          `🎯 Using calculated rental pricing: ₹${rentalPricing.totalAmount} (Base: ₹${rentalPricing.basePrice} + Tax: ₹${rentalPricing.taxes})`,
        );
        subtotal = rentalPricing.subtotal;
      } else if (serviceType === "airport" && urlAmount > 0) {
        // For airport services, always use the exact URL amount without modifications
        console.log(`✈️ Using exact airport service amount: ₹${urlAmount}`);
        subtotal = urlAmount;
      } else if (urlAmount > 0) {
        console.log(`Using URL-provided amount as actual price: ₹${urlAmount}`);
        subtotal = urlAmount; // This is the actual price (before tax)
      } else {
        console.log(`No URL amount, calculating subtotal manually`);
        const tripDistance = distanceParam || 0;
        const baseKmPrice = parseFloat(cabDetails.baseKmPrice) || 12;
        const baseFare = Math.round(tripDistance * baseKmPrice);
        const includedKm = parseFloat(cabDetails.includedKm) || 0;
        const extraKmRate =
          parseFloat(cabDetails.extraFarePerKm) || baseKmPrice * 1.5;
        const extraKm = Math.max(0, tripDistance - includedKm);
        const extraFare = Math.round(extraKm * extraKmRate);
        subtotal = baseFare + extraFare;
      }

      // Calculate taxes (18% GST) and total amount
      const taxRate = 0.18;
      let amount;
      if (serviceType === "rental" && rentalPricing) {
        // Use the base price from rental pricing, not the total
        amount = rentalPricing.basePrice;
      } else if (serviceType === "airport" && urlAmount > 0) {
        // For airport services, pass the actual price only (let Payment page calculate GST)
        amount = urlAmount;
      } else {
        // For regular outstation services, pass the actual price (subtotal) without GST
        // Payment page will calculate GST on top
        amount = subtotal;
      }

      // For long-distance trips (>100km), ensure the price is realistic (skip for rental and airport services)
      const tripDistance = distanceParam || 0;
      if (
        tripDistance > 100 &&
        serviceType !== "rental" &&
        serviceType !== "airport"
      ) {
        // Determine the appropriate per-km rate based on cab category
        let baseRatePerKm = 12; // Default standard rate

        // Look for category indicators in the cab name or details
        const cabNameLower = cabDetails.name?.toLowerCase() || "";
        if (
          cabNameLower.includes("luxury") ||
          cabNameLower.includes("premium")
        ) {
          baseRatePerKm = 15; // Premium/luxury rate
        } else if (cabNameLower.includes("suv")) {
          baseRatePerKm = 14; // SUV rate
        }

        // Calculate the minimum realistic fare for this distance (before taxes)
        const minRealisticSubtotal = Math.round(tripDistance * baseRatePerKm);
        // Calculate minimum realistic total with taxes
        const minRealisticTaxes = Math.round(minRealisticSubtotal * taxRate);
        const minRealisticTotal = minRealisticSubtotal + minRealisticTaxes;

        // If the current total amount is unrealistically low (less than 80% of calculated),
        if (amount < minRealisticTotal * 0.8) {
          console.log(
            `Adjusting fare from ₹${amount} to more realistic ₹${minRealisticTotal} for ${tripDistance}km`,
          );
          // Adjust the amount to a realistic value
          params.append("amount", minRealisticTotal.toString());
        } else {
          // Use the calculated amount
          params.append("amount", amount.toString());
        }
      } else {
        // For shorter trips, use the calculated amount directly
        params.append("amount", amount.toString());
      }
    } else {
      // If no cab details, use a fallback amount or URL amount if available
      const fallbackAmount = queryParams.get("amount")
        ? queryParams.get("amount")
        : "1200";
      console.log(`Using fallback amount: ₹${fallbackAmount}`);
      params.append("amount", fallbackAmount);
    }

    // Log the final URL parameters for debugging
    console.log(
      "Navigating to payment with parameters:",
      Object.fromEntries(params.entries()),
    );

    // Navigate to payment page with all parameters
    navigate(`/booking/payment?${params.toString()}`);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 flex justify-center items-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Customer Details</h1>
          <p className="text-gray-600">
            Please provide your contact information to proceed with the booking
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <form
              onSubmit={handleSubmit}
              className="bg-white rounded-lg shadow-md p-6"
            >
              <div className="space-y-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary focus-within:border-primary">
                    <div className="bg-light p-3 border-r">
                      <FaUser className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="fullName"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      placeholder="Enter your full name"
                      className={`flex-1 p-3 outline-none ${errors.fullName ? "border-accent" : ""}`}
                      aria-invalid={!!errors.fullName}
                      aria-describedby={
                        errors.fullName ? "fullName-error" : undefined
                      }
                    />
                  </div>
                  {errors.fullName && (
                    <p className="text-accent text-sm mt-1">
                      {errors.fullName}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary focus-within:border-primary">
                    <div className="bg-gray-50 p-3 border-r flex items-center">
                      <FaPhone className="text-gray-400 mr-1" />
                      <span className="text-gray-500">+91</span>
                    </div>
                    <input
                      type="tel"
                      name="mobile"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      placeholder="10-digit mobile number"
                      className={`flex-1 p-3 outline-none ${errors.mobile ? "border-accent" : ""}`}
                      maxLength={10}
                      aria-invalid={!!errors.mobile}
                      aria-describedby={
                        errors.mobile ? "mobile-error" : undefined
                      }
                    />
                  </div>
                  {errors.mobile && (
                    <p className="text-accent text-sm mt-1">{errors.mobile}</p>
                  )}
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    Email Address{" "}
                    <span className="text-gray-500 text-sm">(optional)</span>
                  </label>
                  <div className="flex items-center border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary focus-within:border-primary">
                    <div className="bg-light p-3 border-r">
                      <FaEnvelope className="text-gray-400" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className={`flex-1 p-3 outline-none ${errors.email ? "border-accent" : ""}`}
                      aria-invalid={!!errors.email}
                      aria-describedby={
                        errors.email ? "email-error" : undefined
                      }
                    />
                  </div>
                  {errors.email && (
                    <p className="text-accent text-sm mt-1">{errors.email}</p>
                  )}
                  <p className="text-gray-500 text-sm mt-1">
                    We'll send booking confirmation to this email
                  </p>
                </div>

                <div>
                  <label className="block text-gray-700 font-medium mb-2">
                    {serviceType === "airport" && airportDirection === "to"
                      ? "Choose pickup city"
                      : "Pickup Address"}{" "}
                    <span className="text-accent">*</span>
                  </label>
                  <div className="flex items-start border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                    <div className="bg-gray-50 p-3 border-r h-full">
                      <FaMapMarkerAlt className="text-gray-400" />
                    </div>
                    <textarea
                      value={pickupAddress}
                      onChange={(e) => {
                        setPickupAddress(e.target.value);
                        // Only set city address if it's empty (for initial setup)
                        if (!pickupAddressCity) {
                          setPickupAddressCity(fromCity || e.target.value);
                        }
                      }}
                      placeholder={
                        serviceType === "airport" && airportDirection === "to"
                          ? `Choose pickup city if user selects this in booking form`
                          : `Enter complete pickup address in ${fromCity}`
                      }
                      className="flex-1 p-3 outline-none min-h-[80px]"
                      disabled={
                        serviceType === "airport" && airportDirection === "from"
                      }
                    />
                  </div>
                  {errors.pickupAddress && (
                    <p className="text-accent text-sm mt-1">
                      {errors.pickupAddress}
                    </p>
                  )}
                  {fromParam && !pickupAddress && (
                    <p className="text-primary text-sm mt-1">
                      Suggested pickup: {fromParam}
                    </p>
                  )}
                </div>

                {/* Hide drop address for rental and airport services */}
                {serviceType !== "rental" && serviceType !== "airport" && (
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">
                      Drop Address (optional)
                    </label>
                    <div className="flex items-start border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                      <div className="bg-gray-50 p-3 border-r h-full">
                        <FaMapMarkerAlt className="text-gray-400" />
                      </div>
                      <textarea
                        value={dropAddress}
                        onChange={(e) => {
                          setDropAddress(e.target.value);
                          // Only set city address if it's empty (for initial setup)
                          if (!dropAddressCity) {
                            setDropAddressCity(toCity || e.target.value);
                          }
                        }}
                        placeholder={
                          serviceType === "airport" && airportDirection === "to"
                            ? `Enter drop address in ${toCity} (if known)`
                            : `Enter drop address in ${toCity} (if known)`
                        }
                        className="flex-1 p-3 outline-none min-h-[80px]"
                        disabled={
                          serviceType === "airport" && airportDirection === "to"
                        }
                      />
                    </div>
                    {toParam && !dropAddress && (
                      <p className="text-primary text-sm mt-1">
                        Suggested drop: {toParam}
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-8">
                <button
                  type="submit"
                  className="w-full bg-primary text-white py-3 px-4 rounded-lg font-medium hover:bg-amber-600 transition-colors"
                >
                  Proceed to Payment
                </button>
              </div>
            </form>
          </div>

          <div className="md:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
              <h2 className="text-xl font-bold mb-4">Booking Summary</h2>

              {cabDetails && (
                <div className="mb-4">
                  <img
                    src={
                      cabDetails.imageUrl ||
                      "https://via.placeholder.com/300x200?text=Cab+Image"
                    }
                    alt={cabDetails.name}
                    className="w-full h-auto rounded-lg object-cover mb-3"
                  />
                  <h3 className="font-bold">{cabDetails.name}</h3>
                  <p className="text-gray-600 text-sm">
                    {cabDetails.description}
                  </p>

                  {cabDetails.seatingCapacity && (
                    <div className="flex items-center mt-2 text-sm text-gray-600">
                      <FaUser className="mr-1" />
                      <span>{cabDetails.seatingCapacity} Passengers</span>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-3 mb-6">
                <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg mb-2 shadow-sm">
                  <span className="text-gray-700 font-medium flex items-center">
                    <span className="mr-2 text-blue-500">
                      {serviceType === "rental" ? "🎁" : "📍"}
                    </span>
                    {serviceType === "rental" ? "Package:" : "Route:"}
                  </span>
                  <span className="font-semibold text-blue-600 text-sm sm:text-base">
                    {serviceType === "rental"
                      ? packageName || "Local Rental"
                      : `${fromCity} → ${toCity}`}
                  </span>
                </div>

                {/* Show city for rental services */}
                {serviceType === "rental" && (
                  <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg mb-2 shadow-sm">
                    <span className="text-gray-700 font-medium flex items-center">
                      <span className="mr-2 text-green-500">🏙️</span>
                      City:
                    </span>
                    <span className="font-semibold text-green-600 text-sm sm:text-base">
                      {cityName || fromCity || "Local Service"}
                    </span>
                  </div>
                )}

                {/* Show trip type for all services */}
                <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg mb-2 shadow-sm">
                  <span className="text-gray-700 font-medium flex items-center">
                    <span className="mr-2 text-purple-500">🚗</span>
                    Trip Type:
                  </span>
                  <span className="font-semibold text-purple-600 text-sm sm:text-base">
                    {serviceType === "rental"
                      ? "Local Rental"
                      : serviceType === "airport"
                      ? "Airport Transfer"
                      : bookingType === "roundTrip"
                      ? "Round Trip"
                      : "One Way"}
                  </span>
                </div>

                {/* Show airport direction for airport services */}
                {serviceType === "airport" && airportDirection && (
                  <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg mb-2 shadow-sm">
                    <span className="text-gray-700 font-medium flex items-center">
                      <span className="mr-2 text-blue-500">✈️</span>
                      Direction:
                    </span>
                    <span className="font-semibold text-blue-600 text-sm sm:text-base">
                      {airportDirection === "to" ? "To Airport" : "From Airport"}
                    </span>
                  </div>
                )}

                {/* Show distance and time only for outstation services, not for rental or airport */}
                {serviceType !== "rental" &&
                  serviceType !== "airport" &&
                  distanceParam > 0 && (
                    <>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-gray-600">Distance:</span>
                        <span className="font-medium">
                          {Math.round(distanceParam).toLocaleString("en-IN")} km
                        </span>
                      </div>

                      <div className="flex justify-between items-center mb-1">
                        <span className="text-gray-600">Estimated Time:</span>
                        <span className="font-medium">
                          {(() => {
                            // Calculate estimated time based on ROUNDED distance for consistency
                            // Assuming average speed of 60 km/h
                            const roundedDistance = Math.round(distanceParam);
                            const avgSpeed = 60;
                            const hours = Math.floor(roundedDistance / avgSpeed);
                            const minutes = Math.round(
                              ((roundedDistance % avgSpeed) / avgSpeed) * 60,
                            );

                            // Format the time
                            if (hours > 0) {
                              return `${hours} hr ${minutes} min`;
                            }
                            return `${minutes} min`;
                          })()}
                        </span>
                      </div>
                    </>
                  )}

                {travelDate && (
                  <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg mb-2 shadow-sm">
                    <span className="text-gray-700 font-medium flex items-center">
                      <span className="mr-2 text-orange-500">📅</span>
                      Pickup Date:
                    </span>
                    <span className="font-semibold text-orange-600">
                      {travelDate
                        ? formatDateDDMMYYYY(travelDate)
                        : "Not set"}
                    </span>
                  </div>
                )}

                {travelTime && (
                  <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg mb-2 shadow-sm">
                    <span className="text-gray-700 font-medium flex items-center">
                      <span className="mr-2 text-red-500">🕒</span>
                      Pickup Time:
                    </span>
                    <span className="font-semibold text-red-600">
                      {travelTime
                        ? convertTo12HourFormat(decodeURIComponent(travelTime))
                        : "Time not specified"}
                    </span>
                  </div>
                )}

                {/* Hide Trip Type for rental and airport services */}
                {serviceType !== "rental" && serviceType !== "airport" && (
                  <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg mb-2 shadow-sm">
                    <span className="text-gray-700 font-medium flex items-center">
                      <span className="mr-2 text-indigo-500">🚕</span>
                      Trip Type:
                    </span>
                    <span className="font-semibold text-indigo-600 capitalize">
                      {bookingType || "One Way"}
                    </span>
                  </div>
                )}
              </div>

              <div className="border-t pt-4">

                {/* Included kilometers if applicable */}
                {cabDetails?.includedKm > 0 && (
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Included kilometers:</span>
                    <span className="font-medium">
                      {cabDetails.includedKm} km
                    </span>
                  </div>
                )}

                {/* Extra fare - different for rental vs outstation, hide for airport */}
                {serviceType === "rental" ? (
                  // For rental services, we'll need to get extra fare per hour from the package data
                  // This would typically be passed in the URL or fetched from the package
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Extra Fare/Hour:</span>
                    <span className="font-medium">
                      ₹
                      {(() => {
                        // Get real extra fare per hour from rental package data
                        if (selectedPackage && cabDetails?.name) {
                          console.log("Looking for extra fare for cab:", cabDetails.name, "in package:", selectedPackage.name);

                          // First check service cities for cab-specific extra fare per hour
                          if (selectedPackage.serviceCities && selectedPackage.serviceCities.length > 0) {
                            for (const serviceCity of selectedPackage.serviceCities) {
                              console.log("Checking service city:", serviceCity.cityName, "extraFarePerHour:", serviceCity.extraFarePerHour);

                              if (serviceCity.extraFarePerHour) {
                                // Try exact match first
                                let extraFarePerHour = serviceCity.extraFarePerHour[cabDetails.name];

                                // If no exact match, try variations of the cab name
                                if (!extraFarePerHour) {
                                  // Try with (AC) suffix
                                  const cabWithAC = `${cabDetails.name} (AC)`;
                                  extraFarePerHour = serviceCity.extraFarePerHour[cabWithAC];
                                }

                                if (!extraFarePerHour) {
                                  // Try uppercase version
                                  const cabUpperCase = cabDetails.name.toUpperCase();
                                  extraFarePerHour = serviceCity.extraFarePerHour[cabUpperCase];
                                }

                                if (!extraFarePerHour) {
                                  // Try uppercase with (AC)
                                  const cabUpperCaseAC = `${cabDetails.name.toUpperCase()} (AC)`;
                                  extraFarePerHour = serviceCity.extraFarePerHour[cabUpperCaseAC];
                                }

                                console.log("Found extra fare for", cabDetails.name, ":", extraFarePerHour);
                                if (extraFarePerHour) {
                                  return Number(extraFarePerHour).toLocaleString("en-IN");
                                }
                              }
                            }
                          }

                          // Fallback to legacy cab type pricing
                          if (selectedPackage.cabTypePrices && selectedPackage.cabTypePrices.length > 0) {
                            const cabTypePricing = selectedPackage.cabTypePrices.find(
                              (cp) => (cabDetails.name && cp.cabTypeId?.name === cabDetails.name) || cp.cabTypeId?._id === cabDetails._id
                            );
                            if (cabTypePricing && cabTypePricing.extraFarePerHour) {
                              return Number(cabTypePricing.extraFarePerHour).toLocaleString("en-IN");
                            }
                          }

                          // Package-level extra fare per hour as fallback
                          if (selectedPackage.extraFarePerHour) {
                            return Number(selectedPackage.extraFarePerHour).toLocaleString("en-IN");
                          }
                        }

                        // Final fallback to URL params or default
                        const extraFarePerHour = queryParams.get("extraFarePerHour") || "50";
                        return Number(extraFarePerHour).toLocaleString("en-IN");
                      })()}
                    </span>
                  </div>
                ) : serviceType !== "airport" ? (
                  // For outstation services, show extra fare per km (but not for airport)
                  cabDetails?.extraFarePerKm > 0 && (
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600">Extra fare/km:</span>
                      <span className="font-medium">
                        ₹
                        {Number(cabDetails.extraFarePerKm).toLocaleString(
                          "en-IN",
                        )}
                      </span>
                    </div>
                  )
                ) : null}

                {/* Calculate and display price details with proper validation */}
                {(() => {
                  // ✅ Use pre-calculated distanceParam (either known or from URL)
                  const distance = distanceParam || 0;

                  // Check if amount is passed in URL - if so, use it as the total fare
                  const urlAmount = queryParams.get("amount")
                    ? parseFloat(queryParams.get("amount"))
                    : 0;

                  // For rental services, use dynamic pricing from package data
                  if (serviceType === "rental" && selectedPackage) {
                    console.log("🎁 Using rental service pricing...");
                    console.log("Selected package:", selectedPackage);

                    console.log("Cab details:", cabDetails);
                    console.log("🔍 Debugging cab matching...");
                    console.log("Current cab name:", cabDetails?.name);

                    // Find the pricing for this specific cab type in the package
                    let totalPriceFromPackage = cabDetails?.basePrice || 5; // Use cab's base price or fallback

                    // Check for NEW serviceCities structure first (from admin panel)
                    let foundPrice = false;
                    if (
                      selectedPackage.serviceCities &&
                      Array.isArray(selectedPackage.serviceCities)
                    ) {
                      console.log("🔍 Checking serviceCities for pricing...");
                      selectedPackage.serviceCities.forEach(
                        (serviceCity, index) => {
                          console.log(
                            `📍 Service city ${index + 1}:`,
                            serviceCity.cityName,
                          );
                          if (serviceCity.cabPrices) {
                            console.log(
                              "💰 Available cab prices:",
                              serviceCity.cabPrices,
                            );

                            // Try to find pricing for current cab
                            const currentCabName = cabDetails?.name || "";
                            console.log("🔍 Looking for cab:", currentCabName);

                            // Check different cab name variations
                            const cabPrices = serviceCity.cabPrices;
                            const price =
                              cabPrices[currentCabName] ||
                              cabPrices[currentCabName.toUpperCase()] ||
                              cabPrices[currentCabName.toLowerCase()] ||
                              cabPrices[
                                currentCabName.replace(/\s*\(AC\)/i, "")
                              ] ||
                              cabPrices[
                                currentCabName
                                  .replace(/\s*\(AC\)/i, "")
                                  .toUpperCase()
                              ];

                            if (price && price > 0) {
                              totalPriceFromPackage = price;
                              foundPrice = true;
                              console.log(
                                `🎉 SUCCESS! Found pricing in serviceCities: ₹${totalPriceFromPackage} for ${currentCabName}`,
                              );
                              return;
                            }
                          }
                        },
                      );
                    }

                    // Fallback to legacy cabTypePrices structure if no serviceCities pricing found
                    if (!foundPrice) {
                      const cabPricesArray =
                        selectedPackage.cabTypePrices || selectedPackage.cabs;

                      if (
                        cabPricesArray &&
                        Array.isArray(cabPricesArray) &&
                        cabPricesArray.length > 0
                      ) {
                        console.log("✅ Cab prices array exists and is valid");
                        // Log all available cabs in the package
                        console.log("📋 Available cabs in package:");
                        cabPricesArray.forEach((cab, index) => {
                          const cabTypeName =
                            cab.cabTypeId?.name || cab.cabType?.name;
                          const cabPrice = cab.price;
                          console.log(
                            `  ${index + 1}. ${cabTypeName} - ₹${cabPrice}`,
                          );
                        });

                        // Look for matching cab in the package
                        const currentCabName =
                          cabDetails?.name?.toLowerCase() || "";
                        console.log(
                          "🔍 Looking for match with:",
                          currentCabName,
                        );

                        const matchingCab = cabPricesArray.find((cab) => {
                          const cabTypeName = (
                            cab.cabTypeId?.name ||
                            cab.cabType?.name ||
                            ""
                          ).toLowerCase();

                          console.log(
                            `🔄 Comparing "${cabTypeName}" with "${currentCabName}"`,
                          );

                          // Try exact match first
                          if (cabTypeName === currentCabName) {
                            console.log("✅ Exact match found!");
                            return true;
                          }

                          // Try partial matches for common variations
                          if (
                            cabTypeName.includes("innova") &&
                            currentCabName.includes("innova")
                          ) {
                            console.log("✅ INNOVA match found!");
                            return true;
                          }
                          if (
                            cabTypeName.includes("sedan") &&
                            currentCabName.includes("sedan")
                          ) {
                            console.log("✅ SEDAN match found!");
                            return true;
                          }
                          if (
                            cabTypeName.includes("hatchback") &&
                            currentCabName.includes("hatchback")
                          ) {
                            console.log("✅ HATCHBACK match found!");
                            return true;
                          }
                          if (
                            cabTypeName.includes("suv") &&
                            currentCabName.includes("suv")
                          ) {
                            console.log("✅ SUV match found!");
                            return true;
                          }

                          // Try reverse matching (current name includes package name)
                          if (
                            currentCabName.includes(cabTypeName) &&
                            cabTypeName.length > 2
                          ) {
                            console.log("✅ Reverse match found!");
                            return true;
                          }

                          console.log("❌ No match found");
                          return false;
                        });

                        if (matchingCab && matchingCab.price) {
                          totalPriceFromPackage = matchingCab.price;
                          foundPrice = true;
                          const cabTypeName =
                            matchingCab.cabTypeId?.name ||
                            matchingCab.cabType?.name;
                          console.log(
                            `🎉 SUCCESS! Found matching cab pricing: ₹${totalPriceFromPackage} for ${cabTypeName}`,
                            matchingCab,
                          );
                        } else {
                          console.log(
                            `❌ FAILED! No matching cab found in package for "${currentCabName}", using fallback price ₹${totalPriceFromPackage}`,
                          );
                          console.log(
                            "Available cab names in package:",
                            cabPricesArray.map(
                              (c) => c.cabTypeId?.name || c.cabType?.name,
                            ),
                          );
                        }
                      }
                    }

                    if (!foundPrice) {
                      console.log(
                        `⚠��  No specific pricing found in package, using cab's base price: ₹${basePrice}`,
                      );
                    }

                    // Calculate base price and taxes from total price
                    let basePrice, taxes, totalAmount;

                    if (foundPrice) {
                      // The price from package is the actual base price
                      // Calculate tax as 18% GST of the base price
                      basePrice = totalPriceFromPackage;
                      const taxRate = 0.18;
                      taxes = Math.round(basePrice * taxRate);

                      // Ensure minimum tax of ₹1
                      if (taxes === 0 && basePrice > 0) {
                        taxes = 1;
                      }

                      totalAmount = basePrice + taxes;
                    } else {
                      // Fallback calculation - treat fallback as base price and add tax
                      basePrice = totalPriceFromPackage;
                      const taxRate = 0.18;
                      taxes = Math.round(basePrice * taxRate);

                      if (taxes === 0 && basePrice > 0) {
                        taxes = 1; // Minimum tax of ₹1 for any non-zero base price
                      }

                      totalAmount = basePrice + taxes;
                      console.log(
                        `⚠️  No specific pricing found in package, using cab's base price: ₹${basePrice}`,
                      );
                    }

                    console.log(
                      `🎁 Rental pricing: Base ₹${basePrice}, Tax ₹${taxes}, Total ₹${totalAmount}`,
                    );

                    return (
                      <>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-600">Actual Price:</span>
                          <span className="font-medium">
                            ₹{basePrice.toLocaleString("en-IN")}
                          </span>
                        </div>

                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-600">GST (18%):</span>
                          <span className="font-medium">
                            ₹{taxes.toLocaleString("en-IN")}
                          </span>
                        </div>

                        <div className="flex justify-between items-center pt-2 border-t mt-2 text-lg">
                          <span className="font-bold">Total Amount:</span>
                          <span className="font-bold text-primary">
                            ₹{totalAmount.toLocaleString("en-IN")}
                          </span>
                        </div>
                      </>
                    );
                  }

                  // For airport services, show proper price breakdown
                  if (serviceType === "airport" && urlAmount > 0) {
                    console.log("✈️ Using airport service pricing...");
                    const baseFare = urlAmount; // URL amount is the base fare, not total
                    const taxRate = 0.18;
                    const taxes = Math.round(baseFare * taxRate);
                    const totalAmount = baseFare + taxes;

                    return (
                      <>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-600">
                            Airport Transfer Fare:
                          </span>
                          <span className="font-medium">
                            ₹{baseFare.toLocaleString("en-IN")}
                          </span>
                        </div>

                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-600">
                            GST (18%):
                          </span>
                          <span className="font-medium">
                            ₹{taxes.toLocaleString("en-IN")}
                          </span>
                        </div>

                        <div className="flex justify-between items-center pt-2 border-t mt-2 text-lg">
                          <span className="font-bold">Total Amount:</span>
                          <span className="font-bold text-primary">
                            ₹{totalAmount.toLocaleString("en-IN")}
                          </span>
                        </div>

                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                          <div className="flex items-center text-sm text-blue-800">
                            <span className="mr-2">✈️</span>
                            <span>
                              Airport transfer service includes driver waiting
                              time and toll charges
                            </span>
                          </div>
                        </div>
                      </>
                    );
                  }

                  // Fallback: If URL has a valid amount, treat it as actual price (before tax)
                  if (urlAmount > 0) {
                    console.log(
                      `Using URL-provided amount as actual price: ₹${urlAmount}`,
                    );
                    // Treat URL amount as actual price (before tax)
                    const actualPrice = urlAmount;
                    // Calculate 18% GST on actual price
                    const taxRate = 0.18;
                    const taxes = Math.round(actualPrice * taxRate);
                    const totalAmount = actualPrice + taxes;

                    return (
                      <>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-600">Actual Price:</span>
                          <span className="font-medium">
                            ₹{actualPrice.toLocaleString("en-IN")}
                          </span>
                        </div>

                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-600">
                            GST (18%):
                          </span>
                          <span className="font-medium">
                            ₹{taxes.toLocaleString("en-IN")}
                          </span>
                        </div>

                        <div className="flex justify-between items-center pt-2 border-t mt-2 text-lg">
                          <span className="font-bold">Total Amount:</span>
                          <span className="font-bold text-primary">
                            ₹{totalAmount.toLocaleString("en-IN")}
                          </span>
                        </div>
                      </>
                    );
                  } else {
                    // Fallback to manual calculation if no amount is provided in URL
                    console.log(
                      `No valid amount in URL, calculating fare manually`,
                    );
                    const baseKmPrice =
                      parseFloat(cabDetails?.baseKmPrice) || 12;
                    const baseFare = Math.round(distance * baseKmPrice);

                    const includedKm = parseFloat(cabDetails?.includedKm) || 0;
                    const extraKmRate =
                      parseFloat(cabDetails?.extraFarePerKm) ||
                      baseKmPrice * 1.5;
                    const extraKm = Math.max(0, distance - includedKm);
                    const extraFare = Math.round(extraKm * extraKmRate);

                    const subtotal = baseFare + extraFare;
                    const taxRate = 0.18;
                    const taxes = Math.round(subtotal * taxRate);
                    const totalAmount = subtotal + taxes;

                    return (
                      <>
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-600">Actual Price:</span>
                          <span className="font-medium">
                            ₹{baseFare.toLocaleString("en-IN")}
                          </span>
                        </div>

                        {extraKm > 0 && includedKm > 0 && (
                          <div className="flex justify-between items-center mb-1 text-sm">
                            <span className="text-gray-600 pl-4">
                              • Extra {extraKm.toFixed(0)} km @ ₹{extraKmRate}
                              /km:
                            </span>
                            <span className="font-medium">
                              ₹{extraFare.toLocaleString("en-IN")}
                            </span>
                          </div>
                        )}

                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-600">
                            GST (18%):
                          </span>
                          <span className="font-medium">
                            ₹{taxes.toLocaleString("en-IN")}
                          </span>
                        </div>

                        <div className="flex justify-between items-center pt-2 border-t mt-2 text-lg">
                          <span className="font-bold">Total Amount:</span>
                          <span className="font-bold text-primary">
                            ₹{totalAmount.toLocaleString("en-IN")}
                          </span>
                        </div>
                      </>
                    );
                  }
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDetails;
